import React, { useState, useEffect } from 'react';
import { fetchReceipts, saveReceipt, deleteReceipt } from '../../services/api';
import './DailyReceipt.css';

const MODES = ['Cash', 'UPI', 'NEFT', 'RTGS', 'Cheque', 'Card'];
const today = '04-12-2025';

export default function DailyReceipt({ user }) {
  const companyId = user?.Companyid || user?.company_id || "1";

  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [filterDate, setFilterDate] = useState(today);
  const [form, setForm] = useState({ date: today, mode: 'Cash', description: '', amount: '' });

  useEffect(() => {
    loadData();
  }, [companyId, filterDate]);

  const loadData = async () => {
    setLoading(true);
    try {
      const res = await fetchReceipts({
        Companyid: companyId,
        Name: "",
        FromDate: filterDate,
        ToDate: filterDate
      });
      if (res.success) {
        const raw = res.Response || [];
        const list = Array.isArray(raw) ? raw : [raw];
        setRecords(list.map(r => ({
          id: r.Recid || r.IDS,
          date: r.Date1 || r.date,
          mode: r.PaymentMode || r.mode,
          description: r.Name || r.description,
          amount: parseFloat(r.Amount || r.amount || 0),
          receiptNo: r.BillNo || r.receiptNo
        })));
      }
    } catch (err) {
      console.error("Receipt load error:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!form.description || !form.amount) return;
    
    try {
      const payload = {
        Companyid: companyId,
        Name: form.description,
        Amount: form.amount.toString(),
        Date1: form.date,
        PaymentMode: form.mode,
        IDS: "0"
      };

      const result = await saveReceipt(payload);
      if (result.success || result.Response === "INSERTED") {
        await loadData();
        setShowForm(false);
        setForm({ date: today, mode: 'Cash', description: '', amount: '' });
      } else {
        alert(result.message || "Failed to save receipt");
      }
    } catch (err) {
      console.error("Receipt save error:", err);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this receipt?')) return;
    try {
      const result = await deleteReceipt(id);
      if (result.success || result.Response === "DELETED" || result.status === "Success") {
        await loadData();
      } else {
        alert(result.message || "Failed to delete receipt");
      }
    } catch (err) {
      console.error("Receipt delete error:", err);
    }
  };

  const filtered = records.filter(r => !filterDate || r.date === filterDate);
  const total    = filtered.reduce((s, r) => s + (+r.amount || 0), 0);
  const modeBreakdown = MODES.map(mode => ({
    mode, total: filtered.filter(r => r.mode === mode).reduce((s, r) => s + (+r.amount || 0), 0)
  })).filter(m => m.total > 0);

  return (
    <div className="daily-receipt-page">
      <div className="page-header">
        <h2 className="page-title">Daily Receipt</h2>
        <div style={{ display: 'flex', gap: 8 }}>
          <input className="form-control" style={{ width: 160 }} value={filterDate} onChange={e => setFilterDate(e.target.value)} placeholder="DD-MM-YYYY" />
          <button className="btn btn-primary btn-sm" onClick={() => setShowForm(!showForm)}>＋ Add Receipt</button>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(150px,1fr))', gap: 12, marginBottom: 14 }}>
        <div style={{ background: '#fff', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: '12px 16px', boxShadow: 'var(--shadow-sm)', borderLeft: '4px solid var(--secondary)' }}>
          <div style={{ fontSize: 18, fontWeight: 800, color: 'var(--secondary)' }}>₹{total.toLocaleString()}</div>
          <div style={{ fontSize: 11.5, color: 'var(--text-secondary)', fontWeight: 600 }}>Total Receipts</div>
        </div>

        {modeBreakdown.map(m => (
          <div key={m.mode} style={{ background: '#fff', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: '12px 16px', boxShadow: 'var(--shadow-sm)', borderLeft: '4px solid var(--primary)' }}>
            <div style={{ fontSize: 18, fontWeight: 800, color: 'var(--primary)' }}>₹{m.total.toLocaleString()}</div>
            <div style={{ fontSize: 11.5, color: 'var(--text-secondary)', fontWeight: 600 }}>{m.mode}</div>
          </div>
        ))}
      </div>

      {showForm && (
        <div className="section-card">
          <div className="section-header">Add Receipt</div>
          <div className="form-row">
            <div className="form-group"><label>Date</label><input className="form-control" value={form.date} onChange={e => setForm({ ...form, date: e.target.value })} /></div>
            <div className="form-group"><label>Mode</label><select className="form-control" value={form.mode} onChange={e => setForm({ ...form, mode: e.target.value })}>{MODES.map(m => <option key={m}>{m}</option>)}</select></div>
            <div className="form-group"><label>Description</label><input className="form-control" value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} placeholder="e.g., Cash Sales" /></div>
            <div className="form-group"><label>Amount (₹)</label><input type="number" className="form-control" value={form.amount} onChange={e => setForm({ ...form, amount: e.target.value })} /></div>
          </div>

          <div className="action-bar" style={{ padding: '0 16px 14px' }}>
  <button className="btn btn-primary" onClick={handleSave}>
    <i className="bi bi-save me-1"></i> Save
  </button>

  <button className="btn btn-secondary" onClick={() => setShowForm(false)}>
    <i className="bi bi-x-lg me-1"></i> Cancel
  </button>
</div>
        </div>
      )}

      <div className="section-card">
        <div className="section-header">Receipts — {filtered.length} records</div>
        <div className="table-wrapper">
          <table className="data-table">
            <thead>
              <tr>
                <th className="text-center">Receipt No.</th>
                <th className="text-center">Date</th>
                <th className="text-center">Mode</th>
                <th className="text-left">Description</th>
                <th className="text-right">Amount (₹)</th>
                <th className="text-center">Action</th>
              </tr>
            </thead>

            <tbody>
              {filtered.map((r, i) => (
                <tr key={r.id}>
                  <td className="text-center" style={{ fontFamily: 'monospace', color: 'var(--primary)', fontWeight: 700 }}>{r.receiptNo || '—'}</td>
                  <td className="text-center">{r.date}</td>
                  <td className="text-center">
                    <span className="badge badge-info">{r.mode}</span>
                  </td>
                  <td className="text-left">{r.description}</td>
                  <td className="text-right" style={{ fontWeight: 700 }}>
                    ₹{parseFloat(r.amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </td>
                  <td className="text-center">
                    </td>
                </tr>
              ))}
            </tbody>

          </table>
        </div>
      </div>
    </div>
  );
}